import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import './Leagues.css';

/**
 * Self-enrolment control for a league season. A logged-in player can join a duel
 * league themselves; a team captain can enrol one of their (mode-matching) teams
 * — no admin needed. Hidden for finished seasons, anonymous visitors, players
 * with no linked account, and once the player/team is already registered.
 *
 * @param {object} props - Component props.
 * @param {object} props.league - The league (for mode and participant type).
 * @param {object} props.season - The selected season (id + status).
 * @param {object} props.standings - Standings payload (to detect existing entry).
 * @param {Function} props.onJoined - Called after a successful enrolment.
 */
export default function JoinSeason({ league, season, standings, onJoined }) {
  const { t } = useTranslation();
  const { isLoggedIn, user, getAuthHeaders } = useAuth();
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const teamBased = Boolean(league?.is_team_based);
  const joinable = Boolean(season && season.status !== 'finished' && isLoggedIn && user?.player_id);

  useEffect(() => {
    if (!joinable || !teamBased) return;
    fetch(`${API_URL}/api/leagues/teams?game_mode=${league.game_mode}&is_active=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTeams(Array.isArray(d) ? d : []))
      .catch(() => setTeams([]));
  }, [joinable, teamBased, league?.game_mode]);

  if (!joinable) return null;

  // Already-registered identities from the current standings.
  const divisions = standings?.divisions || [];
  const regPlayers = new Set();
  const regTeams = new Set();
  divisions.forEach((d) => (d.participants || []).forEach((p) => {
    if (p.player_id) regPlayers.add(String(p.player_id));
    if (p.team?.id) regTeams.add(String(p.team.id));
  }));

  const myCaptainTeams = teams.filter(
    (t) => String(t.captain_id) === String(user.player_id) && !regTeams.has(String(t.id)),
  );
  const alreadyIn = !teamBased && regPlayers.has(String(user.player_id));

  const join = async (body) => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/leagues/seasons/${season.id}/join`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        setErr(data?.error || data?.detail || t('leagues.joinFailed'));
        return;
      }
      setMsg(t('leagues.joinDone'));
      if (onJoined) onJoined();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="league-join">
      {err && <span className="league-join-err">{err}</span>}
      {msg ? (
        <span className="league-join-msg">{msg}</span>
      ) : teamBased ? (
        myCaptainTeams.length > 0 ? (
          <div className="league-join-row">
            <select
              className="form-input form-input-sm"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              <option value="">{t('leagues.selectYourTeam')}</option>
              {myCaptainTeams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
            </select>
            <button
              className="btn-primary-sm"
              disabled={busy || !teamId}
              onClick={() => join({ team_id: teamId })}
            >
              {t('leagues.registerTeam')}
            </button>
          </div>
        ) : (
          <span className="league-join-hint">
            {t('leagues.becomeCaptainHint', { mode: league.game_mode_display })}
          </span>
        )
      ) : alreadyIn ? (
        <span className="league-join-hint">{t('leagues.alreadyRegistered')}</span>
      ) : (
        <button className="btn-primary-sm" disabled={busy} onClick={() => join()}>
          {t('leagues.registerSelf')}
        </button>
      )}
    </div>
  );
}
